import { toUint8Array } from 'js-base64'
import { PublicKey } from '@solana/web3.js'
import {
  LayerZeroChainKey,
  LayerZeroChainType,
  LayerZeroDirection,
  LayerZeroTransferStatus,
  LayerZeroValidationCode
} from '../types/layerzero'
import type {
  LayerZeroQuote,
  LayerZeroSession,
  LayerZeroMetadata,
  LayerZeroRouteStep,
  LayerZeroStatusResult,
  LayerZeroChainMetadata,
  LayerZeroExecutionPlan,
  LayerZeroStepValidation,
  LayerZeroTransactionStep,
  LayerZeroEncodedTransaction,
  LayerZeroExecutionHistoryEntry
} from '../types/layerzero'

export const LAYERZERO_INJECTIVE_EVM_CHAIN_ID = 1776
export const LAYERZERO_INJ_EVM_DECIMALS = 18
export const LAYERZERO_INJ_SOLANA_DECIMALS = 9
export const LAYERZERO_MAX_AMOUNT_DIGITS = 78

export const LAYERZERO_INJECTIVE_EVM_NATIVE_SENTINEL =
  '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'
export const LAYERZERO_SOLANA_INJ_OFT_MINT =
  '1NJMqVM4PadjuzYmeB7zV7q7DV8oB3ExaQCd9x6KsLz'

/** Native INJ kept back so the source EVM account can still pay gas. */
export const LAYERZERO_EVM_GAS_BUFFER_INJ = '0.005'

export const LAYERZERO_INJECTIVE_EVM_EXPLORER_URL =
  'https://blockscout.injective.network'
export const LAYERZERO_SOLANA_EXPLORER_URL = 'https://explorer.solana.com'

const EVM_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/
const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/
const QUOTE_ID_REGEX = /^0x[0-9a-fA-F]{64}$/
const EVM_TX_HASH_REGEX = /^0x[0-9a-fA-F]{64}$/
const SOLANA_TX_SIGNATURE_REGEX = /^[1-9A-HJ-NP-Za-km-z]{86,90}$/
const BASE_UNIT_AMOUNT_REGEX = /^(?:0|[1-9][0-9]*)$/
const EVM_CALLDATA_REGEX = /^0x[0-9a-fA-F]*$/
const BASE64_REGEX = /^[A-Za-z0-9+/]+={0,2}$/
const DECIMAL_AMOUNT_REGEX = /^(\d+)(?:\.(\d*))?$/

export class LayerZeroParseError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'LayerZeroParseError'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) {
    throw new LayerZeroParseError(`Missing ${field}`)
  }

  return value
}

function hasUint256Range(value: string): boolean {
  try {
    return BigInt(value) <= (1n << 256n) - 1n
  } catch {
    return false
  }
}

export function isValidEvmAddress(value: unknown): value is string {
  return typeof value === 'string' && EVM_ADDRESS_REGEX.test(value.trim())
}

export function isValidSolanaAddress(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }

  const address = value.trim()

  if (!SOLANA_ADDRESS_REGEX.test(address)) {
    return false
  }

  try {
    return new PublicKey(address).toBase58() === address
  } catch {
    return false
  }
}

export function isValidLayerZeroQuoteId(value: unknown): value is string {
  return typeof value === 'string' && QUOTE_ID_REGEX.test(value.trim())
}

export function isValidLayerZeroTxHash(value: unknown): value is string {
  if (typeof value !== 'string') {
    return false
  }

  const trimmed = value.trim()

  return (
    EVM_TX_HASH_REGEX.test(trimmed) || SOLANA_TX_SIGNATURE_REGEX.test(trimmed)
  )
}

export function isValidLayerZeroBaseUnitAmount(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= LAYERZERO_MAX_AMOUNT_DIGITS &&
    BASE_UNIT_AMOUNT_REGEX.test(value) &&
    hasUint256Range(value)
  )
}

export function isLayerZeroBase64(value: null | string | undefined): boolean {
  if (typeof value !== 'string' || value.length === 0) {
    return false
  }

  return value.length % 4 === 0 && BASE64_REGEX.test(value)
}

export function decodeLayerZeroBase64(value: string): null | Uint8Array {
  if (!isLayerZeroBase64(value)) {
    return null
  }

  try {
    return toUint8Array(value)
  } catch {
    return null
  }
}

export function isSameEvmAddress(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

export function getLayerZeroSourceChainKey(
  direction: LayerZeroDirection
): LayerZeroChainKey {
  return direction === LayerZeroDirection.InjectiveEvmToSolana
    ? LayerZeroChainKey.InjectiveEvm
    : LayerZeroChainKey.Solana
}

export function getLayerZeroDestinationChainKey(
  direction: LayerZeroDirection
): LayerZeroChainKey {
  return direction === LayerZeroDirection.InjectiveEvmToSolana
    ? LayerZeroChainKey.Solana
    : LayerZeroChainKey.InjectiveEvm
}

export function isLayerZeroEvmSource(direction: LayerZeroDirection): boolean {
  return direction === LayerZeroDirection.InjectiveEvmToSolana
}

export function getLayerZeroSourceDecimals(
  direction: LayerZeroDirection
): number {
  return isLayerZeroEvmSource(direction)
    ? LAYERZERO_INJ_EVM_DECIMALS
    : LAYERZERO_INJ_SOLANA_DECIMALS
}

export function getLayerZeroDestinationDecimals(
  direction: LayerZeroDirection
): number {
  return isLayerZeroEvmSource(direction)
    ? LAYERZERO_INJ_SOLANA_DECIMALS
    : LAYERZERO_INJ_EVM_DECIMALS
}

export function buildLayerZeroSolanaRpcUrl(
  alchemyKey?: null | string
): string {
  const key = (alchemyKey || '').trim()

  return key
    ? `https://solana-mainnet.g.alchemy.com/v2/${key}`
    : 'https://api.mainnet-beta.solana.com'
}

export function buildLayerZeroSolanaWsUrl(
  alchemyKey?: null | string
): string {
  const key = (alchemyKey || '').trim()

  return key
    ? `wss://solana-mainnet.streaming.alchemy.com/v2/${key}`
    : 'wss://api.mainnet-beta.solana.com'
}

/**
 * Converts a human INJ amount into the source chain base units. Returns null
 * when the value is not a valid non-negative decimal or has too much precision
 * for the source token.
 */
export function toLayerZeroBaseUnits(
  value: string,
  decimals: number
): null | string {
  const match = DECIMAL_AMOUNT_REGEX.exec(value.trim())

  if (!match) {
    return null
  }

  const whole = match[1]!
  const fraction = match[2] ?? ''

  if (fraction.length > decimals) {
    return null
  }

  try {
    return BigInt(`${whole}${fraction.padEnd(decimals, '0')}`).toString()
  } catch {
    return null
  }
}

/**
 * Formats base units into a decimal string, truncating (never rounding up) to
 * `maxFractionDigits`. Returns null when the value is not a base-unit integer.
 */
export function formatLayerZeroBaseUnits(
  value: string,
  decimals: number,
  maxFractionDigits = decimals
): null | string {
  if (!BASE_UNIT_AMOUNT_REGEX.test(value)) {
    return null
  }

  const amount = BigInt(value)
  const divisor = 10n ** BigInt(decimals)
  const whole = amount / divisor
  const fraction = amount % divisor

  if (fraction === 0n) {
    return whole.toString()
  }

  const fractionString = fraction
    .toString()
    .padStart(decimals, '0')
    .slice(0, Math.max(0, maxFractionDigits))
    .replace(/0+$/, '')

  return fractionString ? `${whole}.${fractionString}` : whole.toString()
}

/**
 * Formats quote amounts that may already be decimal strings. Unknown shapes
 * are passed through untouched so the UI never hides a value.
 */
export function formatLayerZeroQuoteAmount(
  value: string | undefined,
  decimals: number
): null | string {
  if (!value) {
    return null
  }

  return formatLayerZeroBaseUnits(value, decimals) ?? value
}

export function computeLayerZeroMaxAmount({
  balance,
  direction,
  quote,
  gasBufferInj = LAYERZERO_EVM_GAS_BUFFER_INJ
}: {
  balance: bigint
  gasBufferInj?: string
  quote?: null | LayerZeroQuote
  direction: LayerZeroDirection
}): null | string {
  if (balance <= 0n) {
    return null
  }

  if (!isLayerZeroEvmSource(direction)) {
    return formatLayerZeroBaseUnits(
      balance.toString(),
      LAYERZERO_INJ_SOLANA_DECIMALS
    )
  }

  const buffer = toLayerZeroBaseUnits(
    gasBufferInj,
    LAYERZERO_INJ_EVM_DECIMALS
  )

  if (!buffer) {
    return formatLayerZeroBaseUnits(
      balance.toString(),
      LAYERZERO_INJ_EVM_DECIMALS
    )
  }

  const nativeFee = quote ? getLayerZeroEvmNativeFee(quote) : 0n
  const max = balance - BigInt(buffer) - nativeFee

  if (max <= 0n) {
    return null
  }

  return formatLayerZeroBaseUnits(
    max.toString(),
    LAYERZERO_INJ_EVM_DECIMALS
  )
}

/**
 * The native EVM bridge step's value includes the transfer amount plus the
 * LayerZero message fee. Return only the additional native fee so MAX can
 * reserve it without subtracting the transfer amount twice.
 */
export function getLayerZeroEvmNativeFee(quote: LayerZeroQuote): bigint {
  if (!isValidLayerZeroBaseUnitAmount(quote.srcAmount)) {
    return 0n
  }

  const totalValue = quote.userSteps.reduce((total, step) => {
    const value = step.transaction.encoded.value

    return isValidLayerZeroBaseUnitAmount(value) ? total + BigInt(value) : total
  }, 0n)
  const sourceAmount = BigInt(quote.srcAmount)

  return totalValue > sourceAmount ? totalValue - sourceAmount : 0n
}

export function getLayerZeroRequiredEvmBalance(
  quote: LayerZeroQuote,
  gasBufferInj = LAYERZERO_EVM_GAS_BUFFER_INJ
): null | bigint {
  const gasBuffer = toLayerZeroBaseUnits(
    gasBufferInj,
    LAYERZERO_INJ_EVM_DECIMALS
  )

  if (!gasBuffer) {
    return null
  }

  const totalValue = quote.userSteps.reduce((total, step) => {
    const value = step.transaction.encoded.value

    return isValidLayerZeroBaseUnitAmount(value) ? total + BigInt(value) : total
  }, 0n)

  return totalValue + BigInt(gasBuffer)
}

export function isTerminalLayerZeroStatus(
  status: LayerZeroTransferStatus
): boolean {
  return (
    status === LayerZeroTransferStatus.Succeeded ||
    status === LayerZeroTransferStatus.Failed
  )
}

export function isSuccessfulLayerZeroStatus(
  status: LayerZeroTransferStatus
): boolean {
  return status === LayerZeroTransferStatus.Succeeded
}

export function isLayerZeroQuoteExpired(
  quote: Pick<LayerZeroQuote, 'expiresAt'>,
  now = Date.now()
): boolean {
  if (!quote.expiresAt) {
    return false
  }

  const expiresAt = Date.parse(quote.expiresAt)

  if (Number.isNaN(expiresAt)) {
    return false
  }

  return expiresAt <= now
}

export function formatLayerZeroHash(
  value: string,
  prefix = 8,
  suffix = 6
): string {
  if (!value || value.length <= prefix + suffix) {
    return value
  }

  return `${value.slice(0, prefix)}...${value.slice(-suffix)}`
}

export function buildLayerZeroSourceTxUrl(
  direction: LayerZeroDirection,
  hash: string
): null | string {
  if (!isValidLayerZeroTxHash(hash)) {
    return null
  }

  return isLayerZeroEvmSource(direction)
    ? `${LAYERZERO_INJECTIVE_EVM_EXPLORER_URL}/tx/${hash}`
    : `${LAYERZERO_SOLANA_EXPLORER_URL}/tx/${hash}`
}

export function formatLayerZeroDuration(value: unknown): null | string {
  if (typeof value !== 'string' || value.length === 0) {
    return null
  }

  const isoMatch =
    /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?$/.exec(value)

  if (isoMatch) {
    const hours = Number(isoMatch[1] ?? 0)
    const minutes = Number(isoMatch[2] ?? 0)
    const seconds = Number(isoMatch[3] ?? 0)

    return formatLayerZeroSeconds(hours * 3600 + minutes * 60 + seconds)
  }

  // The Value Transfer API returns duration.estimated as milliseconds.
  const milliseconds = Number(value)

  if (Number.isFinite(milliseconds) && milliseconds > 0) {
    return formatLayerZeroSeconds(milliseconds / 1000)
  }

  const shortMatch = /^(\d+)\s*(s|m|h)$/i.exec(value)

  if (shortMatch) {
    const amount = Number(shortMatch[1])
    const unit = shortMatch[2]!.toLowerCase()

    if (unit === 's') {
      return formatLayerZeroSeconds(amount)
    }
    if (unit === 'm') {
      return formatLayerZeroSeconds(amount * 60)
    }

    return formatLayerZeroSeconds(amount * 3600)
  }

  return value
}

function formatLayerZeroSeconds(seconds: number): string {
  if (seconds < 60) {
    return `~${Math.max(1, Math.round(seconds))}s`
  }

  if (seconds < 3600) {
    return `~${Math.round(seconds / 60)}min`
  }

  return `~${Math.round(seconds / 3600)}h`
}

export function getLayerZeroAllowedEvmTargets(
  metadata?: LayerZeroChainMetadata
): string[] {
  // This proxy only exposes native INJ OFT quotes. Native transfers execute
  // through LZMulticall and never need an ERC-20 approval/delegate step.
  return [metadata?.multicall]
    .filter((value): value is string => isValidEvmAddress(value))
    .map((value) => value.toLowerCase())
}

export function validateLayerZeroEvmUserStep({
  metadata,
  signerAddress,
  step
}: {
  signerAddress: string
  step: LayerZeroTransactionStep
  metadata?: LayerZeroChainMetadata
}): LayerZeroStepValidation {
  if (step.type !== 'TRANSACTION') {
    return { ok: false, code: LayerZeroValidationCode.NotTransaction }
  }

  if (
    step.chainType !== LayerZeroChainType.Evm ||
    step.chainKey !== LayerZeroChainKey.InjectiveEvm
  ) {
    return { ok: false, code: LayerZeroValidationCode.ChainMismatch }
  }

  if (
    !isValidEvmAddress(signerAddress) ||
    !isValidEvmAddress(step.signerAddress) ||
    !isSameEvmAddress(step.signerAddress, signerAddress)
  ) {
    return { ok: false, code: LayerZeroValidationCode.SignerMismatch }
  }

  const encoded = step.transaction.encoded

  if (encoded.chainId !== LAYERZERO_INJECTIVE_EVM_CHAIN_ID) {
    return { ok: false, code: LayerZeroValidationCode.InvalidChainId }
  }

  if (!isValidEvmAddress(encoded.to)) {
    return { ok: false, code: LayerZeroValidationCode.InvalidTarget }
  }

  if (!EVM_CALLDATA_REGEX.test(encoded.data)) {
    return { ok: false, code: LayerZeroValidationCode.InvalidCalldata }
  }

  if (
    encoded.from !== undefined &&
    (!isValidEvmAddress(encoded.from) ||
      !isSameEvmAddress(encoded.from, signerAddress))
  ) {
    return { ok: false, code: LayerZeroValidationCode.SignerMismatch }
  }

  if (
    encoded.value !== undefined &&
    !isValidLayerZeroBaseUnitAmount(encoded.value)
  ) {
    return { ok: false, code: LayerZeroValidationCode.InvalidValue }
  }

  const allowedTargets = getLayerZeroAllowedEvmTargets(metadata)

  if (allowedTargets.length === 0) {
    return { ok: false, code: LayerZeroValidationCode.MetadataUnavailable }
  }

  if (!allowedTargets.includes(encoded.to.toLowerCase())) {
    return { ok: false, code: LayerZeroValidationCode.TargetNotAllowed }
  }

  return { ok: true, step }
}

export function validateLayerZeroSolanaUserStep({
  signerAddress,
  step
}: {
  signerAddress: string
  step: LayerZeroTransactionStep
}): LayerZeroStepValidation {
  if (step.type !== 'TRANSACTION') {
    return { ok: false, code: LayerZeroValidationCode.NotTransaction }
  }

  if (
    step.chainType !== LayerZeroChainType.Solana ||
    step.chainKey !== LayerZeroChainKey.Solana
  ) {
    return { ok: false, code: LayerZeroValidationCode.ChainMismatch }
  }

  if (
    !isValidSolanaAddress(signerAddress) ||
    step.signerAddress !== signerAddress
  ) {
    return { ok: false, code: LayerZeroValidationCode.SignerMismatch }
  }

  const encoded = step.transaction.encoded

  if (encoded.encoding !== undefined && encoded.encoding !== 'base64') {
    return { ok: false, code: LayerZeroValidationCode.InvalidEncoding }
  }

  if (!isLayerZeroBase64(encoded.data)) {
    return { ok: false, code: LayerZeroValidationCode.InvalidTransaction }
  }

  return { ok: true, step }
}

/**
 * Validates every execution step before any wallet is asked to sign. EVM
 * steps additionally have their target checked against the LayerZero
 * LZMulticall deployment metadata.
 */
export function validateLayerZeroExecutionSteps({
  direction,
  metadata,
  signerAddress,
  steps
}: {
  signerAddress: string
  direction: LayerZeroDirection
  metadata?: LayerZeroChainMetadata
  steps: LayerZeroTransactionStep[]
}): LayerZeroExecutionPlan {
  if (steps.length === 0) {
    return { ok: false, code: LayerZeroValidationCode.InvalidTransaction }
  }

  const validated: LayerZeroTransactionStep[] = []

  for (const step of steps) {
    const result = isLayerZeroEvmSource(direction)
      ? validateLayerZeroEvmUserStep({ metadata, signerAddress, step })
      : validateLayerZeroSolanaUserStep({ signerAddress, step })

    if (!result.ok) {
      return { ok: false, code: result.code }
    }

    validated.push(result.step)
  }

  return { ok: true, steps: validated }
}

export function selectLayerZeroQuote(
  quotes: LayerZeroQuote[]
): null | LayerZeroQuote {
  if (quotes.length === 0) {
    return null
  }

  const score = (quote: LayerZeroQuote): null | bigint => {
    const value = quote.dstAmountMin ?? quote.dstAmount

    if (!value || !BASE_UNIT_AMOUNT_REGEX.test(value)) {
      return null
    }

    return BigInt(value)
  }

  return quotes.reduce((best, quote) => {
    const bestScore = score(best)
    const quoteScore = score(quote)

    if (quoteScore === null) {
      return best
    }

    if (bestScore === null || quoteScore > bestScore) {
      return quote
    }

    return best
  })
}

function parseRouteStep(value: unknown): LayerZeroRouteStep {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid route step')
  }

  const description = optionalString(value.description)

  return {
    type: requireString(value.type, 'route step type'),
    srcChainKey: requireString(value.srcChainKey, 'route step srcChainKey'),
    ...(description ? { description } : {})
  }
}

function parseFee(value: unknown) {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid quote fee')
  }

  const description = optionalString(value.description)
  const amount = typeof value.amount === 'string' ? value.amount : undefined
  const address = optionalString(value.address)

  return {
    chainKey: requireString(value.chainKey, 'fee chainKey'),
    type: requireString(value.type, 'fee type'),
    ...(description ? { description } : {}),
    ...(amount === undefined ? {} : { amount }),
    ...(address ? { address } : {})
  }
}

function parseDuration(value: unknown) {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid quote duration')
  }

  return {
    estimated:
      value.estimated === null
        ? null
        : typeof value.estimated === 'string'
          ? value.estimated
          : undefined
  }
}

function parseQuote(value: unknown): LayerZeroQuote {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid quote')
  }

  const id = requireString(value.id, 'quote id')

  if (!isValidLayerZeroQuoteId(id)) {
    throw new LayerZeroParseError('Invalid quote id')
  }

  if (
    value.direction !== LayerZeroDirection.InjectiveEvmToSolana &&
    value.direction !== LayerZeroDirection.SolanaToInjectiveEvm
  ) {
    throw new LayerZeroParseError('Invalid quote direction')
  }

  if (!Array.isArray(value.routeSteps)) {
    throw new LayerZeroParseError('Invalid quote route steps')
  }

  if (!Array.isArray(value.userSteps)) {
    throw new LayerZeroParseError('Invalid quote user steps')
  }

  const duration = isRecord(value.duration)
    ? parseDuration(value.duration)
    : undefined
  const options = isRecord(value.options)
    ? parseQuoteOptions(value.options)
    : undefined
  const feeUsd = optionalString(value.feeUsd)
  const feePercent = optionalString(value.feePercent)
  const srcAmount = optionalString(value.srcAmount)
  const dstAmount = optionalString(value.dstAmount)
  const dstAmountMin = optionalString(value.dstAmountMin)
  const srcAmountUsd = optionalString(value.srcAmountUsd)
  const dstAmountUsd = optionalString(value.dstAmountUsd)
  const expiresAt = optionalString(value.expiresAt)

  return {
    id,
    direction: value.direction,
    srcChainKey: requireString(value.srcChainKey, 'quote srcChainKey'),
    dstChainKey: requireString(value.dstChainKey, 'quote dstChainKey'),
    routeSteps: value.routeSteps.map(parseRouteStep),
    userSteps: value.userSteps.map(parseLayerZeroTransactionStep),
    ...(Array.isArray(value.fees)
      ? { fees: value.fees.map(parseFee) }
      : {}),
    ...(duration ? { duration } : {}),
    ...(feeUsd ? { feeUsd } : {}),
    ...(feePercent ? { feePercent } : {}),
    ...(srcAmount ? { srcAmount } : {}),
    ...(dstAmount ? { dstAmount } : {}),
    ...(dstAmountMin ? { dstAmountMin } : {}),
    ...(srcAmountUsd ? { srcAmountUsd } : {}),
    ...(dstAmountUsd ? { dstAmountUsd } : {}),
    ...(options ? { options } : {}),
    ...(expiresAt ? { expiresAt } : {})
  }
}

function parseQuoteOptions(value: Record<string, unknown>) {
  const dstNativeDropAmount =
    typeof value.dstNativeDropAmount === 'string'
      ? value.dstNativeDropAmount
      : undefined

  return dstNativeDropAmount ? { dstNativeDropAmount } : {}
}

function parseEncodedTransaction(value: unknown): LayerZeroEncodedTransaction {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid encoded transaction')
  }

  const data = value.data

  if (typeof data !== 'string' || data.length === 0) {
    throw new LayerZeroParseError('Invalid encoded transaction data')
  }

  const chainId =
    typeof value.chainId === 'number' && Number.isInteger(value.chainId)
      ? value.chainId
      : undefined
  const to = optionalString(value.to)
  const from = optionalString(value.from)

  return {
    data,
    ...(to ? { to } : {}),
    ...(typeof value.value === 'string' ? { value: value.value } : {}),
    ...(chainId === undefined ? {} : { chainId }),
    ...(from ? { from } : {}),
    ...(typeof value.gasLimit === 'string'
      ? { gasLimit: value.gasLimit }
      : {}),
    ...(typeof value.encoding === 'string'
      ? { encoding: value.encoding }
      : {})
  }
}

export function parseLayerZeroTransactionStep(
  value: unknown
): LayerZeroTransactionStep {
  if (!isRecord(value)) {
    throw new LayerZeroParseError('Invalid user step')
  }

  if (value.type !== 'TRANSACTION') {
    throw new LayerZeroParseError('Unsupported user step type')
  }

  if (!isRecord(value.transaction)) {
    throw new LayerZeroParseError('Invalid user step transaction')
  }

  const chainType = value.chainType

  if (
    chainType !== LayerZeroChainType.Evm &&
    chainType !== LayerZeroChainType.Solana &&
    chainType !== LayerZeroChainType.Starknet
  ) {
    throw new LayerZeroParseError('Invalid user step chain type')
  }

  const description = optionalString(value.description)

  return {
    type: 'TRANSACTION',
    chainKey: requireString(value.chainKey, 'user step chainKey'),
    chainType,
    signerAddress: requireString(
      value.signerAddress,
      'user step signer address'
    ),
    transaction: {
      encoded: parseEncodedTransaction(value.transaction.encoded)
    },
    ...(description ? { description } : {})
  }
}

export function parseLayerZeroQuotesResponse(
  payload: unknown
): LayerZeroQuote[] {
  if (
    !isRecord(payload) ||
    !isRecord(payload.data) ||
    !Array.isArray(payload.data.quotes)
  ) {
    throw new LayerZeroParseError('Invalid quotes response')
  }

  return payload.data.quotes.map(parseQuote)
}

export function parseLayerZeroUserStepsResponse(
  payload: unknown
): LayerZeroTransactionStep[] {
  if (
    !isRecord(payload) ||
    !isRecord(payload.data) ||
    !Array.isArray(payload.data.userSteps)
  ) {
    throw new LayerZeroParseError('Invalid user steps response')
  }

  return payload.data.userSteps.map(parseLayerZeroTransactionStep)
}

function parseExecutionHistory(value: unknown): LayerZeroExecutionHistoryEntry[] {
  if (!Array.isArray(value)) {
    throw new LayerZeroParseError('Invalid execution history')
  }

  return value.map((entry) => {
    if (!isRecord(entry) || typeof entry.event !== 'string') {
      throw new LayerZeroParseError('Invalid execution history entry')
    }

    const transaction = entry.transaction

    return {
      event: entry.event,
      ...(isRecord(transaction) &&
      typeof transaction.chainKey === 'string' &&
      typeof transaction.hash === 'string'
        ? {
            transaction: {
              chainKey: transaction.chainKey,
              hash: transaction.hash
            }
          }
        : {})
    }
  })
}

function parseLayerZeroExplorerUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length === 0) {
    return undefined
  }

  try {
    const url = new URL(value)
    const isLayerZeroExplorer =
      url.hostname === 'layerzeroscan.com' ||
      url.hostname.endsWith('.layerzeroscan.com') ||
      url.hostname === 'scan.layerzero.network'

    return url.protocol === 'https:' && isLayerZeroExplorer
      ? url.toString()
      : undefined
  } catch {
    return undefined
  }
}

export function parseLayerZeroStatusResponse(
  payload: unknown
): LayerZeroStatusResult {
  if (
    !isRecord(payload) ||
    !isRecord(payload.data) ||
    typeof payload.data.status !== 'string'
  ) {
    throw new LayerZeroParseError('Invalid status response')
  }

  const status = Object.values(LayerZeroTransferStatus).includes(
    payload.data.status as LayerZeroTransferStatus
  )
    ? (payload.data.status as LayerZeroTransferStatus)
    : LayerZeroTransferStatus.Unknown
  const explorerUrl = parseLayerZeroExplorerUrl(payload.data.explorerUrl)

  return {
    status,
    ...(explorerUrl ? { explorerUrl } : {}),
    ...(payload.data.executionHistory === undefined
      ? {}
      : {
          executionHistory: parseExecutionHistory(
            payload.data.executionHistory
          )
        })
  }
}

function parseChainMetadata(value: unknown): LayerZeroChainMetadata {
  if (!isRecord(value)) {
    return {}
  }

  return {
    ...(optionalString(value.multicall)
      ? { multicall: value.multicall as string }
      : {}),
    ...(optionalString(value.transferDelegate)
      ? { transferDelegate: value.transferDelegate as string }
      : {})
  }
}

export function parseLayerZeroMetadataResponse(
  payload: unknown
): LayerZeroMetadata {
  if (
    !isRecord(payload) ||
    !isRecord(payload.data) ||
    !isRecord(payload.data.chains)
  ) {
    throw new LayerZeroParseError('Invalid metadata response')
  }

  const chains = payload.data.chains

  return {
    chains: {
      [LayerZeroChainKey.InjectiveEvm]: parseChainMetadata(
        chains[LayerZeroChainKey.InjectiveEvm]
      ),
      [LayerZeroChainKey.Solana]: parseChainMetadata(
        chains[LayerZeroChainKey.Solana]
      )
    }
  }
}

export function isLayerZeroSession(value: unknown): value is LayerZeroSession {
  if (!isRecord(value)) {
    return false
  }

  return (
    typeof value.id === 'string' &&
    isValidLayerZeroQuoteId(value.quoteId) &&
    (value.direction === LayerZeroDirection.InjectiveEvmToSolana ||
      value.direction === LayerZeroDirection.SolanaToInjectiveEvm) &&
    isValidLayerZeroBaseUnitAmount(value.amount) &&
    typeof value.amountDisplay === 'string' &&
    typeof value.srcWalletAddress === 'string' &&
    typeof value.dstWalletAddress === 'string' &&
    isValidLayerZeroTxHash(value.srcTxHash) &&
    Array.isArray(value.srcTxHashes) &&
    value.srcTxHashes.every((hash) => isValidLayerZeroTxHash(hash)) &&
    typeof value.createdAt === 'number' &&
    typeof value.updatedAt === 'number' &&
    Object.values(LayerZeroTransferStatus).includes(
      value.status as LayerZeroTransferStatus
    )
  )
}
